/**
 * Deterministic demo-data + formatting layer behind the investor-book popover (PT-5705).
 *
 * There is no per-investor field on `PammOfferType` yet, so — per the ticket's acceptance
 * criteria ("deterministic demo data") — the underlying investor list is synthesised
 * client-side from the offer's id, investor count and total deposit sum (AUM). The output
 * is fully deterministic: the same offer id always yields the same list, with
 *   • funds as whole units that sum exactly to the node AUM,
 *   • shares = funds ÷ AUM × 100 that therefore total exactly 100%,
 *   • rows pre-sorted by funds descending (biggest investor first).
 *
 * When a real investors query lands, swap {@link generateInvestorBook} for it — the popover
 * only depends on the {@link Investor} shape.
 */

/** Fixed "today" so generated join-dates never drift — keeps the list deterministic. */
const REFERENCE_NOW = Date.UTC(2026, 6, 15) // 15 Jul 2026
const DAY_MS = 86_400_000

/** En-dash shown wherever a value is missing or undefined (AC: empty/edge states, PT-5706). */
export const PLACEHOLDER = '–'

const FIRST = [
  'Hiro', 'Elena', 'Jonas', 'Mira', 'Tom', 'Anja', 'Klara', 'Bruno', 'Felix', 'Nora',
  'Marco', 'Lena', 'Ivan', 'Sara', 'Paul', 'Nina', 'Ola', 'Zoe', 'Kai', 'Ruth',
  'Luca', 'Pia', 'Sven', 'Yara', 'Otto',
]
const LAST = ['K.', 'V.', 'P.', 'O.', 'H.', 'F.', 'Z.', 'A.', 'R.', 'T.', 'B.', 'M.', 'S.', 'D.', 'W.', 'N.', 'L.', 'G.']

export type Investor = {
  id: string
  name: string
  /** Whole currency units the investor funds the offer with. */
  funds: number
  /** Percent of node AUM, exact (unrounded); `null` when AUM is 0 (share is undefined, shown as "–"). */
  share: number | null
  /** `null` renders as "–" — tolerates a missing join date from a future real query. */
  created: Date | null
  /** `null`/empty renders as "–" (no broken mailto) — tolerates a missing address. */
  email: string | null
}

export type InvestorBookInput = {
  offerId: string | number
  totalInvestors: number
  aum: number
}

/** FNV-1a — stable hash so an offer id maps to a fixed pseudo-random seed. */
const hash = (value: string): number => {
  let h = 2166136261
  for (let i = 0; i < value.length; i++) h = Math.imul(h ^ value.charCodeAt(i), 16777619)
  return Math.abs(h)
}

/**
 * Distributes `total` whole units across `weights` with the largest-remainder method,
 * so the parts are integers that sum EXACTLY to `total`. This is what makes the Funds
 * column total the AUM and the Share column total 100% with zero drift.
 */
const largestRemainder = (weights: number[], total: number): number[] => {
  const weightSum = weights.reduce((a, b) => a + b, 0) || 1
  const exact = weights.map((w) => (w / weightSum) * total)
  const floors = exact.map(Math.floor)
  let left = total - floors.reduce((a, b) => a + b, 0)
  // Hand the leftover units to the largest fractional parts first.
  const byFraction = exact
    .map((value, index) => ({index, fraction: value - Math.floor(value)}))
    .sort((a, b) => b.fraction - a.fraction)
  const result = floors.slice()
  for (let k = 0; left > 0 && k < byFraction.length; k++, left--) result[byFraction[k].index]++
  return result
}

/**
 * Builds the deterministic investor book for an offer, sorted by funds descending.
 *
 * A node with no investors yields an empty list (the control is disabled and never opens). A
 * node with investors but zero AUM still lists them, with each `share` left `null` (rendered
 * "–") so there is never a division-by-zero, `NaN` or `Infinity` on screen (PT-5706).
 *
 * @param input - The offer id (seed), its investor count and AUM.
 * @returns The investor rows, biggest funder first; empty only when there are no investors.
 */
export const generateInvestorBook = ({offerId, totalInvestors, aum}: InvestorBookInput): Investor[] => {
  const count = Math.floor(totalInvestors)
  if (count <= 0) return []

  const seed = hash(String(offerId))
  const weights = Array.from({length: count}, (_, i) => 0.4 + ((seed >> i) & 0xff) / 255)
  // With zero (or negative) AUM there is nothing to split, so every investor funds 0.
  const funds = largestRemainder(weights, Math.max(aum, 0))

  const investors = weights.map((_, i) => {
    const first = FIRST[(seed + i * 7) % FIRST.length]
    const last = LAST[(seed + i * 13) % LAST.length]
    const daysAgo = (((seed >> (i % 12)) & 0x3ff) % 480) + 5
    const email = `${first.toLowerCase()}.${last.replace('.', '').toLowerCase()}${((seed + i) % 90) + 10}@mail.com`
    return {
      id: `${offerId}-inv-${i}`,
      name: `${first} ${last}`,
      funds: funds[i],
      // Share is undefined when AUM is 0 — keep it `null` (shown "–") rather than NaN/Infinity.
      share: aum > 0 ? (funds[i] / aum) * 100 : null,
      created: new Date(REFERENCE_NOW - daysAgo * DAY_MS),
      email,
    }
  })

  // Default sort: funds descending (biggest investor first), id as a stable tiebreak.
  return investors.sort((a, b) => b.funds - a.funds || (a.id < b.id ? -1 : 1))
}

/** Currency amount with thousands separators and no decimals (AC: "Funds: USD, no decimals"). */
export const formatFunds = (amount: number, currency: string, locale?: string): string =>
  Number.isFinite(amount)
    ? new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    : PLACEHOLDER

/** Share percentage, always 2 decimals (AC); "–" when the share is undefined (e.g. AUM 0). */
export const formatShare = (percent: number | null | undefined): string =>
  percent != null && Number.isFinite(percent) ? `${percent.toFixed(2)}%` : PLACEHOLDER

/**
 * Date as `DD MMM YYYY` in the viewer's locale — the month name is localised but the field
 * order is always day–month–year regardless of the locale's default pattern. A missing or
 * invalid date renders as "–" (PT-5706).
 *
 * @param date - The date to format; `null`/invalid yields "–".
 * @param locale - Optional locale; defaults to the runtime (user) locale.
 */
export const formatCreated = (date: Date | null | undefined, locale?: string): string => {
  if (!date || Number.isNaN(date.getTime())) return PLACEHOLDER
  const parts = new Intl.DateTimeFormat(locale, {day: '2-digit', month: 'short', year: 'numeric'}).formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  return `${get('day')} ${get('month')} ${get('year')}`
}
